// Zero-knowledge-to-server attribute vault — shared types and crypto parameters.
//
// Model: each private attribute is encrypted with its own symmetric key; that key
// is wrapped by the user's MASTER key (DEK). The DEK is itself wrapped into PIN and
// passkey envelopes (stored in user_key_envelopes). The server only ever holds
// ciphertext. This completes the existing user_attributes encryption design
// (encrypted_details + encrypted_symmetric_key).

export const ZK_VAULT_VERSION = 'zk-vault-v1' as const;
export const PBKDF2_ITERATIONS = 600_000 as const;
export const PBKDF2_HASH = 'SHA-256' as const;
export const SALT_BYTES = 32 as const;
export const IV_BYTES = 12 as const;
export const DEK_BYTES = 32 as const;
export const CHALLENGE_BYTES = 32 as const;

export const prfSalt = (hostname: string): string => `${ZK_VAULT_VERSION}:${hostname}`;

// Self-describing AES-GCM blob; ct includes the 128-bit tag.
export interface EncryptedBlob {
  v: 1;
  iv: string; // base64
  ct: string; // base64
}
export type WrappedKey = EncryptedBlob;

// camelCase view of a user_key_envelopes row (master-key envelopes only).
export interface KeyEnvelopeRecord {
  userId: string;
  envelopePin: WrappedKey | null;
  pinSalt: string | null;
  envelopePasskey: WrappedKey | null;
  passkeyId: string | null;
  updatedAt: string | null;
}

export type VaultStatus = 'loading' | 'uninitialized' | 'locked' | 'unlocked' | 'error';

export interface PrfCapability {
  webauthnSupported: boolean;
  platformAuthenticatorAvailable: boolean;
}

// --- Attribute model (mirrors user_attribute_definitions / user_attributes) ---
export type AttributeDataType = 'BOOLEAN' | 'NUMBER' | 'TEXT' | 'SELECTION' | 'IMAGE';

export interface AttributeDefinition {
  id: string;
  category: string;
  label_en: string;
  label_kh: string;
  data_type: AttributeDataType;
  requires_verification?: boolean;
  verification_reward?: number;
}

export type AttributeValue = string | number | boolean | null;

// A user_attributes row as stored (ciphertext).
export interface StoredAttribute {
  attribute_id: string;
  encrypted_details: string;        // EncryptedBlob JSON of the value
  encrypted_symmetric_key: string;  // WrappedKey JSON (per-attribute key wrapped by DEK)
  value_summary: string | null;
  is_verified?: boolean;
}

// Curated selection options for SELECTION-type attributes (UI only).
export const ATTRIBUTE_OPTIONS: Record<string, string[]> = {
  gender: ['Male', 'Female', 'Other', 'Prefer not to say'],
  marital_status: ['Single', 'Married', 'Divorced', 'Widowed', 'Other'],
  monthly_income: ['< $200', '$200–$500', '$500–$1000', '$1000–$2000', '> $2000'],
};
